/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Common Application utilities
 *
 * @file Utils.js
 * @author Mohamed yasar arbath M
 * @since 1.0.0
 */

import { DAYS_MAP, ASSIGNMENT_TYPES, DEFAULT_TAB_ORDER } from './constants';
import * as CoverageDuplicationUtils from './syllabusUtils/coverageDuplicationUtils';
import * as BadgeDisplayUtils from './syllabusUtils/badgeDisplayUtils';

export default class Utils {
  /**
    * Function debounce for performance optimization while repeat excution
    * @param {function} fn
    * @param {millseconds} ms
    */
  static debounce(fn, ms = 500) {
    let timer;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => { fn.apply(this, args); }, ms);
    };
  }

  /**
    * Function to date format MM/DD/YY
    * @param {date} date
    */
  static getDate(date) {
    if (!date) return '';
    const dateFormat = new Date(date);
    // we dont show empty invalid date, earlier it showed 1970
    if (Number.isNaN(dateFormat.getTime())) return '';

    // Format: Aug 03,2025
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[dateFormat.getMonth()];
    const day = String(dateFormat.getDate()).padStart(2, '0');
    const year = dateFormat.getFullYear();

    return `${month} ${day}, ${year}`;
  }

  /**
   * Returns the correct syllabus style hook based on the parent app name.
   * Usage: getSyllabusStyles(parentApp, { aet: useAetSyllabusStyles, onereader: useOneReaderSyllabusStyles, ereader: useEreaderSyllabusStyles }, useAetSyllabusStyles)
   * @param {string} parentApp
   * @param {object} styleHooks - Map of app name to style hook
   * @param {function} defaultHook - Default style hook
   * @returns {object} classes
  */

  static getSyllabusStyles(parentApp, styleHooks, defaultHook) {
    if (!parentApp || typeof parentApp !== 'string') return defaultHook();
    const key = parentApp.toLowerCase();

    return (styleHooks[key] || defaultHook)();
  }

  /**
   * Build a safe RegExp from user input.
   * Supports literal syntax like /pattern/flags, otherwise escapes input.
   * Returns null if input is empty or invalid.
   * @param {string} input
   * @returns {RegExp|null}
   */
  static buildRegex(input) {
    const trimmed = (input || '').trim();
    if (!trimmed) return null;
    const literalMatch = trimmed.match(/^\/(.*)\/([gimsuy]*)$/);
    if (literalMatch) {
      try {
        return new RegExp(literalMatch[1], literalMatch[2]);
      } catch {
        // ignore and fallback
      }
    }
    const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      return new RegExp(escaped, 'i');
    } catch {
      return null;
    }
  }

  // src/components/BulkEdit/utils/dateUtils.js
  /**
   * Distribute assignment due dates evenly across selected weekdays within a date range.
   *
   * @param {Array<Object>} assignments - Array of assignment objects
   * @param {string} startISO - Start date (YYYY-MM-DD)
   * @param {string} endISO - End date (YYYY-MM-DD)
   * @param {Array<string>} selectedDaysLabels - Day labels ['M', 'T', 'W', 'Th', 'F', 'S', 'Su']
   * @param {Object} options - { fillStrategy: 'even'|'round-robin' }
   * @returns {Array<Object>} Assignments with 'date' property added
   */
  static distributeDates(assignments, startISO, endISO, selectedDaysLabels = [], options = {}) {
    const { startTime, endTime } = options;
    // VALIDATION
    if (!assignments?.length) return [];
    if (!startISO || !endISO) throw new Error('Start and end dates required');

    const start = new Date(startISO);
    const end = new Date(endISO);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      throw new Error('Invalid date format');
    }
    if (start > end) throw new Error('Start date must be <= end date');

    // PROCESS SELECTED DAYS
    const dayMap = DAYS_MAP;
    const selectedDays = new Set(
      (selectedDaysLabels || [])
        .map(label => dayMap[label?.trim?.()])
        .filter(dayNumber => dayNumber !== undefined)
    );

    // eg: set of {0:1, 1:1, 3:1} for Su, M, W

    if (selectedDays.size === 0) throw new Error('At least one day must be selected');

    // GENERATE CANDIDATE DATES
    const candidateDates = [];
    const startDayOfWeek = start.getDay();

    // difference in days, inclusive
    const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

    // calculate mathematically
    for (let dayOffset = 0; dayOffset < totalDays; dayOffset += 1) {
      const currentDayOfWeek = (startDayOfWeek + dayOffset) % 7;

      if (selectedDays.has(currentDayOfWeek)) {
        const currentDate = new Date(start);
        currentDate.setDate(start.getDate() + dayOffset);

        if (currentDate <= end) {
          candidateDates.push(currentDate.toISOString().slice(0, 10));
        }
      }
    }

    if (candidateDates.length === 0) {
      throw new Error('No matching weekdays in date range');
    }

    // DISTRIBUTE ASSIGNMENTS
    const assignmentCount = assignments.length;
    const slotCount = candidateDates.length;
    const fillStrategy = options.fillStrategy || 'even';

    return assignments.map((assignment, index) => {
      let slotIndex = 0;

      // Even distribution: spread assignments evenly across slots
      if (slotCount >= assignmentCount && fillStrategy === 'even' && assignmentCount > 1) {
        slotIndex = Math.round((index * (slotCount - 1)) / (assignmentCount - 1));
        // example below: index * (6-1)/(3-1) = index * 5/2
        // Assignment 0: index=0 → 0 * 6/3 = 0 → Date[0] = Mon (first date)
        // Assignment 1: index=1 → 1 * 6/3 = 2 → Date[2] = Fri
        // Assignment 2: index=2 → 2 * 6/3 = 4 → Date[4] = Wed
        // Assignment 3: index=3 → 3 * 6/3 = 6 → Date[6] = Mon (last date)
      // eslint-disable-next-line brace-style
      }
      // Single assignment: use first slot
      else if (slotCount >= assignmentCount && assignmentCount === 1) {
        slotIndex = 0;
      // eslint-disable-next-line brace-style
      }
      // Round-robin: cycle through slots when more assignments than slots
      else {
        slotIndex = index % slotCount;
      }

      return { ...assignment, date: candidateDates[slotIndex], startTime, endTime };
    });
  }

  // finds the node object from toc by id
  static findNodeById = (nodes, targetId) => {
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (node.id === targetId) return node;
      if (node.children && node.children.length > 0) {
        const found = Utils.findNodeById(node.children, targetId);
        if (found) return found;
      }
    }

    return null;
  };

  static mapAssignmentForEdit = (assignment) => {
    if (!assignment) return null;

    return {
      id: assignment.id,
      title: assignment.title,
      date: assignment.date,
      type: ASSIGNMENT_TYPES.HOMEWORK,
      chapters: []
    };
  };

  /**
   * To local string compare
   *
   * @param {*} stringA
   * @param {*} stringB
   * @returns
   */
  static localStringCompare(stringA, stringB) {
    let isSame = false;
    if (typeof stringA === 'string' && typeof stringB === 'string') {
      isSame = stringA.toLowerCase().trim() === stringB.toLowerCase().trim();
    }

    return isSame;
  }

  /**
   * Calculates sections and questions count for selected chapters/modules
   * @param {Object} formData - Form data containing selected chapters
   * @param {Array} chapterMcqMapping - Array of chapter data with questions
   * @param {Array} tocData - Table of contents data for parent-child mapping
   * @returns {Object} - { sections: number, questions: number }
   */
  static calculateSectionsAndQuestions = (formData, chapterMcqMapping, tocData = []) => {
    if (!formData?.chapters?.length) {
      return { sections: 0, questions: 0 };
    }
    let totalQuestions = 0;
    const selectedIds = new Set();
    formData.chapters.forEach(chapter => {
      const chapterId = typeof chapter === 'object' ? chapter.id : chapter;
      selectedIds.add(chapterId);
    });

    // Exclude roots (nodes with children) from sections count
    const rootIds = new Set(
      tocData
        .filter((ch) => ch?.children?.length > 0)
        .map((ch) => (typeof ch === 'object' ? ch.id : ch))
    );
    const sectionsCount = [...selectedIds].filter((id) => !rootIds.has(id)).length;
    if (chapterMcqMapping && Array.isArray(chapterMcqMapping)) {
      chapterMcqMapping.forEach(chapterData => {
        if (chapterData.questions && Array.isArray(chapterData.questions)) {
          chapterData.questions.forEach(question => {
            if (question.pageId && selectedIds.has(question.pageId)) {
              totalQuestions += 1;
            }
          });
        }
      });
    }

    return {
      sections: sectionsCount,
      questions: totalQuestions
    };
  };

  /**
   * Finds the top-level chapter ID for any selected item (chapter or leaf node)
   * @param {Object|string} selectedItem - Selected chapter or leaf node
   * @param {Array} tocData - Table of contents data
   * @returns {string} - Top-level chapter ID
   */
  static getTopLevelChapterId = (selectedItem, tocData) => {
    const itemId = typeof selectedItem === 'object' ? selectedItem.id : selectedItem;

    // Simple loop through top-level chapters
    for (let i = 0; i < tocData.length; i += 1) {
      const chapter = tocData[i];
      const chapterId = typeof chapter === 'object' ? chapter.id : chapter;

      if (chapterId === itemId) {
        return chapterId;
      }
      if (typeof chapter === 'object' && chapter.children) {
        const found = this.findInChapterChildren(chapter.children, itemId);
        if (found) {
          return chapterId;
        }
      }
    }

    return itemId;
  };

  /**
   * Helper to find item in chapter children
   * @param {Array} children - Children array
   * @param {string} targetId - Target ID to find
   * @returns {boolean} - Whether found
   */
  static findInChapterChildren = (children, targetId) => {
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      const childId = typeof child === 'object' ? child.id : child;

      if (childId === targetId) {
        return true;
      }

      if (typeof child === 'object' && child.children) {
        const found = this.findInChapterChildren(child.children, targetId);
        if (found) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Creates textbook coverage data from selected chapters
   * @param {Array} selectedChapters - Array of selected chapter items
   * @param {Array} tocData - Table of contents data
   * @returns {Object} - Textbook coverage object with chapters array
   */
  static createTextbookCoverage = (selectedChapters, tocData) => {
    const chapters = selectedChapters
      .map((selectedItem) => {
        const itemId = typeof selectedItem === 'object' ? selectedItem.id : selectedItem;
        const topLevelChapterId = this.getTopLevelChapterId(selectedItem, tocData);
        const topLevelChapter = tocData.find(tocChapter => {
          const tocId = typeof tocChapter === 'object' ? tocChapter.id : tocChapter;

          return tocId === topLevelChapterId;
        });

        const actualIndex = tocData.findIndex(tocChapter => {
          const tocId = typeof tocChapter === 'object' ? tocChapter.id : tocChapter;

          return tocId === topLevelChapterId;
        });

        return {
          chapterId: topLevelChapterId,
          id: itemId,
          chapterTitle: typeof topLevelChapter === 'object' ? topLevelChapter.title : topLevelChapter,
          num: actualIndex + 1,
          title: typeof topLevelChapter === 'object' ? topLevelChapter.title : topLevelChapter,
          selectedNodeId: itemId,
          selectedNodeType: typeof selectedItem === 'object' ? selectedItem.type : null
        };
      })
      .filter((chapter, index, array) => (
        array.findIndex(item => item.chapterId === chapter.chapterId) === index
      ));
    // Detect partial selections by checking if selected items are subsections
    const partialSections = selectedChapters
      .filter(selectedItem => {
        const itemId = typeof selectedItem === 'object' ? selectedItem.id : selectedItem;
        const topLevelChapterId = this.getTopLevelChapterId(selectedItem, tocData);

        // If the selected item ID doesn't match the top-level chapter ID, it's a subsection
        return itemId !== topLevelChapterId;
      })

      .map(selectedItem => {
        const itemTitle = typeof selectedItem === 'object' ? selectedItem.title : selectedItem;
        const tags = Utils.getAssignmentChaptersNumbers([itemTitle]);

        return tags && tags.length > 0 ? tags[0] : null;
      });

    // Use partial sections if any were detected, otherwise null
    const sections = partialSections.length > 0 ? partialSections : null;
    const coverageIds = Utils.getCoveragePageIdsForPayload(selectedChapters, tocData);

    return {
      chapters,
      sections,
      pages: [...coverageIds],
      sectionIds: []
    };
  };

  /**
   * Appends a node and all its descendants to a collection, deduped by id.
   * @param {Object} node - TOC node to append
   * @param {Array} collection - Target array for selected nodes
   * @param {Set} addedIds - Set of already-added node ids
   */
  static appendNodeAndDescendants = (node, collection, addedIds) => {
    if (!node || addedIds.has(node.id)) return;

    collection.push(node);
    addedIds.add(node.id);

    if (node.children?.length) {
      node.children.forEach((child) => {
        Utils.appendNodeAndDescendants(child, collection, addedIds);
      });
    }
  };

  /**
   * Expands selected chapters to include only the specific nodes that were selected
   * Uses selectedNodeId to identify which exact node was clicked
   * @param {Array} storedChapters - Array of stored chapter objects with id/chapterId and selectedNodeId
   * @param {Array} tocData - Table of contents data structure
   * @returns {Array} - Array with only the specifically selected nodes
   */
  static expandChaptersWithDescendants = (storedChapters, tocData) => {
    if (!storedChapters || !Array.isArray(storedChapters)) return [];

    const allSelectedChapters = [];
    const addedIds = new Set();

    storedChapters.forEach(storedChapter => {
      const chapterId = storedChapter.id || storedChapter.chapterId;
      const selectedNodeId = storedChapter.selectedNodeId;

      // Find the specific node that was selected
      const findSpecificNode = (nodes, targetId = selectedNodeId || chapterId) => {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];

          if (node.id === targetId) {
            return node;
          }

          if (node.children && node.children.length > 0) {
            const found = findSpecificNode(node.children, targetId);
            if (found) return found;
          }
        }

        return null;
      };

      const selectedNode = findSpecificNode(tocData);

      if (selectedNode) {
        Utils.appendNodeAndDescendants(selectedNode, allSelectedChapters, addedIds);
      }
    });

    return allSelectedChapters;
  };

  /**
   * Rehydrate selected nodes for editing from textbookCoverage.
   * - When preferPages is true and pages are present, return the exact selected page nodes.
   * - Otherwise, fall back to expanding chapters with descendants.
   * @param {Object} textbookCoverage
   * @param {Array} tocData
   * @param {Object} options
   * @returns {Array}
   */
  static rehydrateSelectedNodesFromCoverage = (textbookCoverage, tocData, { preferPages } = {}) => {
    if (!textbookCoverage) return [];

    const storedChapters = Utils.asCoverageArray(textbookCoverage.chapters);
    const storedPages = Utils.asCoverageArray(textbookCoverage.pages);
    const sectionIds = Utils.asCoverageArray(textbookCoverage.sectionIds);
    const safeToc = Array.isArray(tocData) ? tocData : [];

    if (sectionIds.length > 0) {
      const selected = [];
      const addedIds = new Set();
      sectionIds.forEach((sectionId) => {
        const node = Utils.findNodeById(tocData, sectionId);
        if (node) {
          Utils.appendNodeAndDescendants(node, selected, addedIds);
        }
      });

      storedChapters.forEach((storedChapter) => {
        if (!CoverageDuplicationUtils.isFullyAssignedChapterOnRow(storedChapter, sectionIds, safeToc)) {
          return;
        }
        const chapterId = CoverageDuplicationUtils.getChapterRefId(storedChapter);
        const chapterNode = Utils.findNodeById(tocData, chapterId);
        if (chapterNode) {
          Utils.appendNodeAndDescendants(chapterNode, selected, addedIds);
        }
      });

      return selected;
    }

    if (preferPages && Array.isArray(storedPages) && storedPages.length > 0) {
      const added = new Set();
      const selected = [];
      storedPages.forEach((pageId) => {
        const id = Utils.toNodeId(pageId);
        const node = Utils.findNodeById(tocData, id);
        if (node && !added.has(node.id)) {
          selected.push(node);
          added.add(node.id);
        }
      });

      return selected;
    }

    return Utils.expandChaptersWithDescendants(storedChapters, tocData);
  };

  /**
   * augments toc structure with field isSectionAssigned, to handle restriction logic for chapters in add
   */
  static markAssignedSections = (tocData, assignedSectionIds) => {
    const markRecursive = (nodes) => nodes.map(node => {
      const isAssigned = assignedSectionIds.includes(node.id);

      const children = node.children ? markRecursive(node.children) : undefined;

      return {
        ...node,
        isSectionAssigned: isAssigned,
        ...(children && { children })
      };
    });

    return markRecursive(tocData);
  };

  // recursive helper function for above feature
  static isNodeOrDescendantAssigned = (node) => {
    if (node.isSectionAssigned) return true;
    if (node.children && node.children.length > 0) {
      return node.children.some(child => Utils.isNodeOrDescendantAssigned(child));
    }

    return false;
  };

  /**
   * Checks if all descendants of a node are assigned
   * @param {Object} node - The node to check
   * @returns {boolean} - True if all descendants are assigned, false otherwise
   */
  static isAllDescendantsAssigned = (node) => {
    // Parent: check actual children, not the node's own flag
    if (node.children && node.children.length > 0) {
      return node.children.every(child => Utils.isAllDescendantsAssigned(child));
    }

    // Leaf: check the node's own flag
    return !!node.isSectionAssigned;
  };

  /**
   * Checks if some descendants of a node are assigned
   * @param {Object} node - The node to check
   * @returns {boolean} - True if some descendants are assigned, false otherwise
   */
  static hasSomeAssignedDescendants = (node) => {
    if (node.isSectionAssigned) return true;

    if (node.children && node.children.length > 0) {
      return node.children.some(child => Utils.hasSomeAssignedDescendants(child));
    }

    return false;
  };

  /**
   * Converts a given status to a normalized label by lowercasing and removing all whitespace.
   * @param {string} status - The status value to normalize.
   * @returns {string} Normalized status label.
   */
  static normalizeStatus(status) {
    return status?.toLowerCase().replace(/\s/g, '') || '';
  }

  static asCoverageArray = CoverageDuplicationUtils.asCoverageArray;

  static validateAssignmentChapters = CoverageDuplicationUtils.validateAssignmentChapters;

  static getTabularData = BadgeDisplayUtils.getTabularData;

  static getAssignmentChaptersNumbers = BadgeDisplayUtils.getAssignmentChaptersNumbers;

  static resolveDisplayTitles = BadgeDisplayUtils.resolveDisplayTitles;

  /**
   * Method to return a sanitize string
   * @param {string} input.
   * @returns {string}
   */
  static sanitizeString(input = '') {
    if (!input) {
      return '';
    }

    return input.replace(/[^a-zA-Z0-9]/g, '');
  }

  // Helper to get the rendered tab index for a specific tab ID
  static getTabIndexById(tabId, tabOrder, defaultIndex = -1) {
    const order = tabOrder || DEFAULT_TAB_ORDER;
    const tab = order.find(t => t.id === tabId);

    return tab ? tab.index : defaultIndex;
  }

  static isTabIdAtIndex(tabId, index, tabOrder) {
    const order = tabOrder || DEFAULT_TAB_ORDER;
    const tab = order.find(t => t.index === index);

    return tab?.id === tabId;
  }

  /**
   * Helper to get selected item shape (object/string) into a node id.
   */
  static toNodeId = (item) => (typeof item === 'object' ? item.id : item);

  /**
   * Returns unique selected node ids from current selection.
   */
  static getSelectedNodeIds = (items = []) => new Set(
    items
      .map(Utils.toNodeId)
      .filter(Boolean)
  );


  /**
 * Walks the TOC tree bottom-up. For each selected leaf, adds it to coverageIds.
 * Parent nodes are auto-added only if they have a covered descendant AND
 * are not already assigned in another assignment (prevents duplicate root entries in pages).
 */
  static markAncestorsAsCovered = (node, coverageIds, directSelectedIds) => {
    const isDirectlySelected = directSelectedIds.has(node?.id);
    if (!node?.children?.length) {
      if (isDirectlySelected) coverageIds.add(node.id);

      return isDirectlySelected;
    }
    // Visit ALL children so every selected leaf gets added
    let hasCoveredDescendant = false;
    node.children.forEach(child => {
      if (Utils.markAncestorsAsCovered(child, coverageIds, directSelectedIds)) {
        hasCoveredDescendant = true;
      }
    });
    // Auto-add parent only if not already in a previous assignment's pages
    if ((isDirectlySelected || hasCoveredDescendant) && !node.isSectionAssigned) {
      coverageIds.add(node.id);
    }

    return isDirectlySelected || hasCoveredDescendant;
  };

  /**
 * Main function to pass any level root/subroot also into
 * pages list irrespective of mcqs present or not
 */
  static getCoveragePageIdsForPayload = (selectedItems = [], tocData = []) => {
    const directSelectedIds = Utils.getSelectedNodeIds(selectedItems);
    const coverageIds = new Set();
    tocData.forEach(node => {
      Utils.markAncestorsAsCovered(node, coverageIds, directSelectedIds);
    });

    return coverageIds;
  };

  /**
   * Calculates responsive card width based on container size by determining column count, accounting for gaps, and evenly distributing available space.
   *
   * @param {Number} containerWidth
   * @returns {String} - The sorted width
   */
  static getItemWidth = (containerWidth = 1024, gap = 12) => {
    let columns = 1;

    if (containerWidth >= 1296) {
      columns = 4;
    } else if (containerWidth >= 820) {
      columns = 3;
    } else if (containerWidth >= 500) {
      columns = 2;
    }

    const totalGapWidth = gap * (columns - 1);
    const availableWidth = containerWidth - totalGapWidth;
    const cardWidth = availableWidth / columns;

    return `${cardWidth}px`;
  };
}
